import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

import {
	Accordion,
	AccordionBody,
	AccordionHeader,
	Box,
	Form,
	Stack,
	Text,
} from '@ekhodealer/ekho-common/rev';

import { CopyableCodeBlock } from '@common/components/CopyableCodeBlock';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { formatPhoneNumberDisplay } from '@common/utils/formatPhoneNumber';

import { useGetAiBdcSettings } from '../api/useGetAiBdcSettings';
import { useUpdateAiBdcSettings } from '../api/useUpdateAiBdcSettings';

import './ChannelsSection.scss';

// --- Website Assistant channel ---

const websiteAssistantSchema = z.object({
	enabled: z.boolean().default(false),
});

function WebsiteAssistantChannel() {
	const spId = useCurrentServicesProviderId();
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const dealerName = servicesProvider?.name ?? '';

	const snippet = `<script src="https://cdn.ekhodealer.com/ai/ekho-chat.iife.js"></script>
<script>
  EkhoChat.init({
    dealerId: "${spId}",
    providerId: "${spId}",
    env: "production",
  });
</script>`;

	const handleSubmit = async (
		data: z.infer<typeof websiteAssistantSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					enabled: data.enabled,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	const installGuide = (
		<Accordion className='install-guide-accordion'>
			<AccordionHeader>
				<Text size='bodySmall' weight='semibold'>
					Installation instructions
				</Text>
			</AccordionHeader>
			<AccordionBody itemsSpacing='16'>
				<Text size='bodySmall' color='secondary'>
					Copy the code below and paste it into {dealerName ? `${dealerName}'s` : 'your'}{' '}
					website header — the section that appears on every page. If {"you're"} not sure
					where that is, ask your web provider to add it for you. It{"'"}s lightweight and{' '}
					{"won't"} slow down your site.
				</Text>
				<CopyableCodeBlock label='HTML' value={snippet} />
				<Text size='caption' color='tertiary'>
					That's it — no build step required. The widget loads asynchronously via a tiny
					IIFE wrapper and fetches your branding, display name, and chat configuration
					automatically from your AI BDC settings.
				</Text>
			</AccordionBody>
		</Accordion>
	);

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-website-assistant-channel'
				schema={websiteAssistantSchema}
				defaultValues={{
					enabled: settings?.embeddedWebsiteAssistant?.enabled ?? false,
				}}
				onSubmit={handleSubmit}
				title='Website assistant'
				titleLevel='subgroup'
				titleWeight='semibold'
				titleMaxWidth='full'
				description='When enabled, the AI chat widget appears on your website. Disabling this removes the widget without affecting other AI BDC settings.'
				tag={{ children: 'Recommended', accent: 1, appearance: 'solid', size: 'small' }}
				toggle={{
					fieldName: 'enabled',
					inactiveContent: installGuide,
				}}
				showSaveAndClear>
				{installGuide}
			</Form>
		</Box>
	);
}

// --- SMS channel ---

const smsChannelSchema = z.object({
	smsEnabled: z.boolean().default(false),
});

function SmsPhoneNumberDisplay(): React.ReactNode {
	const { control } = useFormContext();
	const smsEnabled = useWatch({ control, name: 'smsEnabled' });
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const phoneNumber = settings?.sms?.twilioPhone;

	if (!smsEnabled) return null;

	return (
		<Stack itemsSpacing='4'>
			<Text size='bodySmall' weight='semibold'>
				Your AI BDC phone number
			</Text>
			<Text size='bodySmall' color={phoneNumber ? 'secondary' : 'tertiary'}>
				{formatPhoneNumberDisplay(phoneNumber) ?? 'Provisioning in progress'}
			</Text>
		</Stack>
	);
}

function SmsChannel() {
	const spId = useCurrentServicesProviderId();
	const { data: settings } = useGetAiBdcSettings(spId);
	const updateMutation = useUpdateAiBdcSettings(spId);

	const phoneNumber = settings?.sms?.twilioPhone;

	const handleSubmit = async (
		data: z.infer<typeof smsChannelSchema>,
	): Promise<{ isSuccess: boolean }> => {
		try {
			await updateMutation.mutateAsync({
				sms: { ...settings?.sms, enabled: data.smsEnabled },
				// Sync text off-ramp with SMS state: turns on when SMS turns on, off when SMS turns off.
				// Users can still independently disable the off-ramp from Chat Experience settings.
				embeddedWebsiteAssistant: {
					...settings?.embeddedWebsiteAssistant,
					enableTextOffRamp: data.smsEnabled,
				},
			});
			return { isSuccess: true };
		} catch {
			return { isSuccess: false };
		}
	};

	const description =
		'When enabled, the AI will automatically respond to inbound SMS leads. Disabling this pauses all AI-driven text conversations without affecting website chat.';

	// Disable the toggle until a Twilio phone number has been provisioned
	if (!phoneNumber) {
		return (
			<Box padding='24' background='secondary' rounding='12'>
				<div data-settings-disabled>
					<Form
						name='ai-bdc-sms-channel'
						schema={smsChannelSchema}
						defaultValues={{ smsEnabled: false }}
						onSubmit={async () => ({ isSuccess: false })}
						title='SMS'
						titleLevel='subgroup'
						titleWeight='semibold'
						titleMaxWidth='full'
						description={description}
						tag={{ children: 'Recommended', accent: 1, appearance: 'solid', size: 'small' }}
						toggle={{ fieldName: 'smsEnabled' }}>
						<Text size='bodySmall' color='secondary'>
							A dedicated phone number is being provisioned. SMS can be enabled once complete.
						</Text>
					</Form>
				</div>
			</Box>
		);
	}

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='ai-bdc-sms-channel'
				schema={smsChannelSchema}
				defaultValues={{ smsEnabled: settings?.sms?.enabled ?? false }}
				onSubmit={handleSubmit}
				title='SMS'
				titleLevel='subgroup'
				titleWeight='semibold'
				titleMaxWidth='full'
				description={description}
				tag={{ children: 'Recommended', accent: 1, appearance: 'solid', size: 'small' }}
				toggle={{ fieldName: 'smsEnabled' }}
				showSaveAndClear>
				<SmsPhoneNumberDisplay />
			</Form>
		</Box>
	);
}

// --- Email channel (coming soon) ---

const emailChannelSchema = z.object({
	emailEnabled: z.boolean().default(false),
});

function EmailChannel() {
	return (
		<Box padding='24' background='secondary' rounding='12'>
			<div data-settings-disabled>
				<Form
					name='ai-bdc-email-channel'
					schema={emailChannelSchema}
					defaultValues={{ emailEnabled: false }}
					onSubmit={async () => ({ isSuccess: false })}
					title='Email'
					titleLevel='subgroup'
					titleWeight='semibold'
					titleMaxWidth='full'
					description='When enabled, the AI will automatically respond to inbound email leads — including website form submissions, third-party marketplace inquiries, and direct emails. It also allows the website assistant to offer buyers an email off-ramp, so conversations that start on your site can continue seamlessly over email.'
					tag={{ children: 'Coming soon', size: 'small' }}
					toggle={{ fieldName: 'emailEnabled' }}
					showSaveAndClear>
					{null}
				</Form>
			</div>
		</Box>
	);
}

// --- Combined export ---

export function ChannelsSection() {
	return (
		<Stack itemsSpacing='16'>
			<WebsiteAssistantChannel />
			<SmsChannel />
			<EmailChannel />
		</Stack>
	);
}
